import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ConfirmDialog,
  CONFIRM_DIALOG_TONE_DANGER,
  IconButton,
  ICON_BUTTON_VARIANT_GHOST,
  PromoBanner,
  PROMO_BANNER_TONE_SUCCESS,
  PROMO_BANNER_TONE_ERROR,
  Stepper,
  Tooltip,
  TOOLTIP_PLACEMENT_TOP,
} from '@aibuild/ui';
import useSnackbar from '@hooks/useSnackbar';
import Dialog, {
  DIALOG_SIZE_VARIANT_MEDIUM,
} from '@components/2-molecules/Dialog';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import ButtonAtom, {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_OUTLINE,
} from '@components/1-atoms/Button';
import Switch from '@components/1-atoms/Switch';
import {
  listOrgTools,
  createOrgTool,
  updateOrgTool,
  deleteOrgTool,
  testOrgTool,
} from '@api/orgTools';
import type { OrgTool, OrgToolTestResult } from '@api/orgTools';
import { Frame, TableContainer } from '../Settings.styled';
import {
  Wrapper,
  Description,
  ToolTable,
  MonoText,
  StatusCell,
  EmptyState,
  FormBody,
  FieldGroup,
  FieldLabel,
  TextInput,
  Select,
  TextArea,
  FormError,
  FieldError,
  HeaderRow,
  Actions,
  CertSection,
  SectionTitle,
  HelpText,
  FileInput,
  StepperBar,
  LabelRow,
  HelpIcon,
  TestRow,
  DocLink,
} from './OrgTools.styled';

interface FieldLabelWithHelpProps {
  label: string;
  help: string;
}

const FieldLabelWithHelp: React.FC<FieldLabelWithHelpProps> = ({
  label,
  help,
}) => (
  <LabelRow>
    <FieldLabel>{label}</FieldLabel>
    <Tooltip content={help} placement={TOOLTIP_PLACEMENT_TOP} portal>
      <HelpIcon className="material-symbols-rounded" aria-label={help}>
        info
      </HelpIcon>
    </Tooltip>
  </LabelRow>
);

const WIZARD_STEPS = [
  { id: 'details', label: 'Details' },
  { id: 'config', label: 'Config' },
  { id: 'auth', label: 'Auth' },
];
const LAST_STEP = WIZARD_STEPS.length - 1;

const DEFAULT_INPUT_SCHEMA = `{
  "type": "object",
  "properties": {
    "query": { "type": "string" }
  },
  "required": ["query"]
}`;

const DEFAULT_CONFIG = `{
  "http_method": "POST",
  "url_template": "https://api.example.com/endpoint",
  "headers_template": { "Content-Type": "application/json" },
  "body_template": { "input": "{{args.query}}" },
  "response_path": "$.output",
  "auth": { "type": "header", "header": "Authorization", "scheme": "Bearer" },
  "tls_trust": "system"
}`;

const NAME_PATTERN = /^[a-z0-9_]+$/;
const NAME_MAX_LENGTH = 64;

interface FormState {
  name: string;
  description: string;
  kind: string;
  inputSchema: string;
  config: string;
  timeoutMs: string;
  enabled: boolean;
  secret: string;
  clientCert: string;
  clientKey: string;
  clientKeyPassword: string;
}

function emptyForm(): FormState {
  return {
    name: '',
    description: '',
    kind: 'http',
    inputSchema: DEFAULT_INPUT_SCHEMA,
    config: DEFAULT_CONFIG,
    timeoutMs: '30000',
    enabled: true,
    secret: '',
    clientCert: '',
    clientKey: '',
    clientKeyPassword: '',
  };
}

function toForm(tool: OrgTool): FormState {
  return {
    name: tool.name,
    description: tool.description,
    kind: tool.kind,
    inputSchema: JSON.stringify(tool.inputSchema, null, 2),
    config: JSON.stringify(tool.config, null, 2),
    timeoutMs: String(tool.timeoutMs),
    enabled: tool.enabled,
    secret: '',
    clientCert: '',
    clientKey: '',
    clientKeyPassword: '',
  };
}

function buildSecret(values: FormState): Record<string, unknown> | undefined {
  const cert = values.clientCert.trim();
  const key = values.clientKey.trim();
  if (cert && key) {
    const secret: Record<string, unknown> = {
      client_cert: cert,
      client_key: key,
    };
    if (values.clientKeyPassword) {
      secret.client_key_password = values.clientKeyPassword;
    }
    return secret;
  }
  return values.secret.trim().length > 0
    ? parseJsonField('secret', values.secret)
    : undefined;
}

function parseJsonField(label: string, value: string): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error(`${label} must be valid JSON`);
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object`);
  }
  return parsed as Record<string, unknown>;
}

function jsonObjectField(label: string, optional = false) {
  return Yup.string().test('json-object', '', function jsonObjectTest(value) {
    if (optional && (!value || value.trim().length === 0)) {
      return true;
    }
    try {
      const parsed = JSON.parse(value ?? '');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return this.createError({ message: `${label} must be a JSON object` });
      }
      return true;
    } catch {
      return this.createError({ message: `${label} must be valid JSON` });
    }
  });
}

const VALIDATION_SCHEMA = Yup.object({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .max(NAME_MAX_LENGTH, `Name must be at most ${NAME_MAX_LENGTH} characters`)
    .matches(
      NAME_PATTERN,
      'Name may only contain lowercase letters, digits and underscores (a-z, 0-9, _)',
    ),
  description: Yup.string().trim().required('Description is required'),
  kind: Yup.string().required('Kind is required'),
  inputSchema: jsonObjectField('input_schema'),
  config: jsonObjectField('config'),
  timeoutMs: Yup.string().test(
    'positive-number',
    'timeout_ms must be a positive number',
    (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0;
    },
  ),
  secret: jsonObjectField('secret', true),
  clientCert: Yup.string(),
  clientKey: Yup.string().test(
    'cert-key-pair',
    'Client certificate and key must both be provided',
    function certKeyPair(value) {
      const cert = (this.parent.clientCert ?? '').trim();
      const key = (value ?? '').trim();
      return !((cert && !key) || (!cert && key));
    },
  ),
  clientKeyPassword: Yup.string(),
});

function formatTestMeta(result: OrgToolTestResult): string | undefined {
  const parts: string[] = [];
  if (result.statusCode != null) {
    parts.push(`HTTP ${result.statusCode}`);
  }
  if (result.ok) {
    parts.push(`${result.latencyMs}ms`);
  }
  return parts.length > 0 ? parts.join(' \u00b7 ') : undefined;
}

const STEP_FIELDS: Array<Array<keyof FormState>> = [
  ['name', 'description'],
  ['inputSchema', 'config', 'timeoutMs'],
  ['secret', 'clientCert', 'clientKey', 'clientKeyPassword'],
];

const ALL_TOUCHED: Record<string, boolean> = {
  name: true,
  description: true,
  kind: true,
  inputSchema: true,
  config: true,
  timeoutMs: true,
  secret: true,
  clientCert: true,
  clientKey: true,
  clientKeyPassword: true,
};

const OrgTools: React.FC = () => {
  const intl = useIntl();
  const { showSuccess, showError } = useSnackbar();

  const [tools, setTools] = useState<OrgTool[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgTool | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<OrgToolTestResult | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgTool | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadTools = useCallback(async () => {
    try {
      setTools(await listOrgTools());
    } catch {
      showError(
        intl.formatMessage({
          id: 'orgTools.loadError',
          defaultMessage: 'Failed to load tools.',
        }),
      );
    }
  }, [intl, showError]);

  useEffect(() => {
    loadTools();
  }, [loadTools]);

  const formik = useFormik<FormState>({
    initialValues: emptyForm(),
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values) => {
      setApiError(null);
      const payload = {
        description: values.description,
        inputSchema: parseJsonField('input_schema', values.inputSchema),
        kind: values.kind,
        config: parseJsonField('config', values.config),
        timeoutMs: Number(values.timeoutMs),
        enabled: values.enabled,
        secret: buildSecret(values),
      };
      try {
        if (editing) {
          await updateOrgTool(editing.id, payload);
        } else {
          await createOrgTool({ ...payload, name: values.name.trim() });
        }
        setDialogOpen(false);
        showSuccess(
          intl.formatMessage({
            id: 'orgTools.saved',
            defaultMessage: 'Tool saved.',
          }),
        );
        await loadTools();
      } catch (err) {
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? 'Failed to save tool.';
        setApiError(message);
      }
    },
  });

  const { resetForm } = formik;

  const openCreate = useCallback(() => {
    setEditing(null);
    resetForm({ values: emptyForm() });
    setApiError(null);
    setStep(0);
    setTestResult(null);
    setDialogOpen(true);
  }, [resetForm]);

  const openEdit = useCallback(
    (tool: OrgTool) => {
      setEditing(tool);
      resetForm({ values: toForm(tool) });
      setApiError(null);
      setStep(0);
      setTestResult(null);
      setDialogOpen(true);
    },
    [resetForm],
  );

  const handleToggleEnabled = useCallback(
    async (tool: OrgTool, next: boolean) => {
      setTogglingId(tool.id);
      setTools((prev) =>
        prev.map((t) => (t.id === tool.id ? { ...t, enabled: next } : t)),
      );
      try {
        await updateOrgTool(tool.id, {
          description: tool.description,
          inputSchema: tool.inputSchema,
          kind: tool.kind,
          config: tool.config,
          timeoutMs: tool.timeoutMs,
          enabled: next,
        });
      } catch {
        setTools((prev) =>
          prev.map((t) => (t.id === tool.id ? { ...t, enabled: !next } : t)),
        );
        showError(
          intl.formatMessage({
            id: 'orgTools.toggleError',
            defaultMessage: 'Failed to update tool status.',
          }),
        );
      } finally {
        setTogglingId(null);
      }
    },
    [intl, showError],
  );

  const {
    validateForm,
    setTouched,
    setFieldValue,
    submitForm,
    values: formValues,
  } = formik;

  const readFileInto = useCallback(
    (field: 'clientCert' | 'clientKey') =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await setFieldValue(field, await file.text());
      },
    [setFieldValue],
  );

  const handleTest = useCallback(async () => {
    setApiError(null);
    setTestResult(null);
    const errors = await validateForm();
    const configFields: Array<keyof FormState> = [
      'inputSchema',
      'config',
      'timeoutMs',
      'secret',
      'clientCert',
      'clientKey',
      'clientKeyPassword',
    ];
    if (configFields.some((field) => errors[field])) {
      await setTouched(ALL_TOUCHED, false);
      return;
    }
    setTesting(true);
    try {
      const result = await testOrgTool({
        kind: formValues.kind,
        config: parseJsonField('config', formValues.config),
        inputSchema: parseJsonField('input_schema', formValues.inputSchema),
        timeoutMs: Number(formValues.timeoutMs),
        secret: buildSecret(formValues),
        toolId: editing?.id,
      });
      setTestResult(result);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message ?? 'Failed to run the test.';
      setApiError(message);
    } finally {
      setTesting(false);
    }
  }, [validateForm, setTouched, formValues, editing]);

  const goToStep = useCallback((target: number) => {
    setApiError(null);
    setStep(target);
  }, []);

  const handleNext = useCallback(async () => {
    const errors = await validateForm();
    const fields = STEP_FIELDS[step] ?? [];
    const stepTouched = fields.reduce<Record<string, boolean>>(
      (acc, field) => ({ ...acc, [field]: true }),
      {},
    );
    await setTouched({ ...formik.touched, ...stepTouched }, false);
    if (fields.some((field) => errors[field])) {
      return;
    }
    setApiError(null);
    setStep((prev) => Math.min(prev + 1, LAST_STEP));
  }, [validateForm, setTouched, formik.touched, step]);

  const handlePrimary = useCallback(async () => {
    if (step < LAST_STEP) {
      await handleNext();
      return;
    }
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      await setTouched(ALL_TOUCHED, false);
      const firstBadStep = STEP_FIELDS.findIndex((fields) =>
        fields.some((field) => errors[field]),
      );
      if (firstBadStep >= 0) {
        setStep(firstBadStep);
      }
      return;
    }
    await submitForm();
  }, [step, handleNext, validateForm, setTouched, submitForm]);

  const handleSecondary = useCallback(() => {
    if (step === 0) {
      setDialogOpen(false);
    } else {
      goToStep(step - 1);
    }
  }, [step, goToStep]);

  const renderFieldError = useCallback(
    (field: keyof FormState) => {
      const error = formik.errors[field];
      if (formik.touched[field] && typeof error === 'string') {
        return (
          <FieldError data-testid={`org-tool-error-${field}`}>
            {error}
          </FieldError>
        );
      }
      return null;
    },
    [formik.errors, formik.touched],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteOrgTool(deleteTarget.id);
      showSuccess(
        intl.formatMessage({
          id: 'orgTools.deleted',
          defaultMessage: 'Tool deleted.',
        }),
      );
      await loadTools();
    } catch {
      showError(
        intl.formatMessage({
          id: 'orgTools.deleteError',
          defaultMessage: 'Failed to delete tool.',
        }),
      );
    }
  }, [deleteTarget, intl, loadTools, showError, showSuccess]);

  const dialogTitle = useMemo(
    () =>
      editing
        ? intl.formatMessage({ id: 'orgTools.edit', defaultMessage: 'Edit Tool' })
        : intl.formatMessage({ id: 'orgTools.create', defaultMessage: 'New Tool' }),
    [editing, intl],
  );

  const stepperSteps = useMemo(
    () =>
      WIZARD_STEPS.map((s) => ({
        id: s.id,
        label: intl.formatMessage({
          id: `orgTools.step.${s.id}`,
          defaultMessage: s.label,
        }),
      })),
    [intl],
  );

  return (
    <Wrapper data-testid="org-tools-page">
      <Description>
        {intl.formatMessage({
          id: 'orgTools.description',
          defaultMessage:
            'Define custom tools your organization\u2019s AI agents can call. Secrets are encrypted and never shown after saving.',
        })}
      </Description>

      <DocLink
        href="/docs/custom-tools.html"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="org-tools-docs-link"
      >
        <span className="material-symbols-rounded" aria-hidden>
          menu_book
        </span>
        {intl.formatMessage({
          id: 'orgTools.docsLink',
          defaultMessage: 'View documentation',
        })}
      </DocLink>

      <HeaderRow>
        <ButtonAtom
          variant={BUTTON_VARIANT_PRIMARY}
          onClick={openCreate}
          stretch={false}
        >
          {intl.formatMessage({ id: 'orgTools.new', defaultMessage: 'New Tool' })}
        </ButtonAtom>
      </HeaderRow>

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({
          id: 'orgTools.title',
          defaultMessage: 'Custom Tools',
        })}
      />

      <Frame>
        {tools.length > 0 ? (
          <TableContainer>
            <ToolTable>
              <thead>
                <tr>
                  <th>
                    {intl.formatMessage({
                      id: 'orgTools.col.name',
                      defaultMessage: 'Name',
                    })}
                  </th>
                  <th>
                    {intl.formatMessage({
                      id: 'orgTools.col.kind',
                      defaultMessage: 'Kind',
                    })}
                  </th>
                  <th>
                    {intl.formatMessage({
                      id: 'orgTools.col.enabled',
                      defaultMessage: 'Enabled',
                    })}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tools.map((tool) => (
                  <tr key={tool.id}>
                    <td>
                      <MonoText>{tool.name}</MonoText>
                    </td>
                    <td>{tool.kind}</td>
                    <td>
                      <StatusCell>
                        <Switch
                          enabled={tool.enabled}
                          disabled={togglingId === tool.id}
                          onChange={(next: boolean) =>
                            handleToggleEnabled(tool, next)
                          }
                          dataTestId={`org-tool-toggle-${tool.name}`}
                        />
                      </StatusCell>
                    </td>
                    <td>
                      <Actions>
                        <IconButton
                          variant={ICON_BUTTON_VARIANT_GHOST}
                          ariaLabel={intl.formatMessage({
                            id: 'orgTools.editAction',
                            defaultMessage: 'Edit',
                          })}
                          tooltip={intl.formatMessage({
                            id: 'orgTools.editAction',
                            defaultMessage: 'Edit',
                          })}
                          icon={
                            <span
                              className="material-symbols-rounded"
                              aria-hidden
                            >
                              edit
                            </span>
                          }
                          onClick={() => openEdit(tool)}
                          dataTestId={`org-tool-edit-${tool.name}`}
                        />
                        <IconButton
                          variant={ICON_BUTTON_VARIANT_GHOST}
                          ariaLabel={intl.formatMessage({
                            id: 'orgTools.deleteAction',
                            defaultMessage: 'Delete',
                          })}
                          tooltip={intl.formatMessage({
                            id: 'orgTools.deleteAction',
                            defaultMessage: 'Delete',
                          })}
                          icon={
                            <span
                              className="material-symbols-rounded"
                              aria-hidden
                            >
                              delete
                            </span>
                          }
                          onClick={() => setDeleteTarget(tool)}
                          dataTestId={`org-tool-delete-${tool.name}`}
                        />
                      </Actions>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ToolTable>
          </TableContainer>
        ) : (
          <EmptyState>
            {intl.formatMessage({
              id: 'orgTools.empty',
              defaultMessage: 'No custom tools yet. Create one to get started.',
            })}
          </EmptyState>
        )}
      </Frame>

      {dialogOpen && (
        <Dialog
          show={dialogOpen}
          headerTitle={dialogTitle}
          size={DIALOG_SIZE_VARIANT_MEDIUM}
          onClose={() => setDialogOpen(false)}
          onCloseIconButtonClick={() => setDialogOpen(false)}
          primaryButtonLabel={
            step < LAST_STEP
              ? intl.formatMessage({
                  id: 'orgTools.next',
                  defaultMessage: 'Next',
                })
              : intl.formatMessage({
                  id: 'orgTools.save',
                  defaultMessage: 'Save',
                })
          }
          onPrimaryButtonClick={handlePrimary}
          primaryButtonDisabled={formik.isSubmitting}
          secondaryButtonLabel={
            step === 0
              ? intl.formatMessage({
                  id: 'orgTools.cancel',
                  defaultMessage: 'Cancel',
                })
              : intl.formatMessage({
                  id: 'orgTools.back',
                  defaultMessage: 'Back',
                })
          }
          onSecondaryButtonClick={handleSecondary}
          closeOnEscape
        >
          <FormBody onSubmit={(e) => e.preventDefault()}>
            <StepperBar>
              <Stepper
                steps={stepperSteps}
                current={step}
                onStepClick={goToStep}
                ariaLabel={intl.formatMessage({
                  id: 'orgTools.wizard.progress',
                  defaultMessage: 'Tool setup progress',
                })}
              />
            </StepperBar>

            {step === 0 && (
              <>
                {!editing && (
                  <FieldGroup>
                    <FieldLabelWithHelp
                      label={intl.formatMessage({
                        id: 'orgTools.field.name',
                        defaultMessage: 'Name (a-z, 0-9, _)',
                      })}
                      help={intl.formatMessage({
                        id: 'orgTools.help.name',
                        defaultMessage:
                          'Unique identifier the model calls the tool by. Lowercase letters, digits and underscores only (e.g. query_knowledge_base). Cannot be changed later.',
                      })}
                    />
                    <TextInput
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="query_knowledge_base"
                      maxLength={NAME_MAX_LENGTH}
                      $error={!!(formik.touched.name && formik.errors.name)}
                    />
                    {renderFieldError('name')}
                  </FieldGroup>
                )}
                <FieldGroup>
                  <FieldLabelWithHelp
                    label={intl.formatMessage({
                      id: 'orgTools.field.description',
                      defaultMessage: 'Description (shown to the model)',
                    })}
                    help={intl.formatMessage({
                      id: 'orgTools.help.description',
                      defaultMessage:
                        'Tells the model what the tool does and when to use it. Be specific — this drives whether the model picks the tool.',
                    })}
                  />
                  <TextArea
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    $error={
                      !!(formik.touched.description && formik.errors.description)
                    }
                  />
                  {renderFieldError('description')}
                </FieldGroup>
                <FieldGroup>
                  <FieldLabelWithHelp
                    label={intl.formatMessage({
                      id: 'orgTools.field.kind',
                      defaultMessage: 'Kind',
                    })}
                    help={intl.formatMessage({
                      id: 'orgTools.help.kind',
                      defaultMessage:
                        'Execution backend for the tool. "http" calls an external HTTP(S) endpoint.',
                    })}
                  />
                  <Select
                    name="kind"
                    value={formik.values.kind}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="http">http</option>
                  </Select>
                </FieldGroup>
              </>
            )}

            {step === 1 && (
              <>
                <FieldGroup>
                  <FieldLabelWithHelp
                    label={intl.formatMessage({
                      id: 'orgTools.field.inputSchema',
                      defaultMessage: 'Input Schema (JSON Schema)',
                    })}
                    help={intl.formatMessage({
                      id: 'orgTools.help.inputSchema',
                      defaultMessage:
                        'JSON Schema describing the arguments the model must supply. These values fill {{args.*}} placeholders in the URL, headers and body.',
                    })}
                  />
                  <TextArea
                    name="inputSchema"
                    value={formik.values.inputSchema}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    $tall
                    $error={
                      !!(formik.touched.inputSchema && formik.errors.inputSchema)
                    }
                  />
                  {renderFieldError('inputSchema')}
                </FieldGroup>
                <FieldGroup>
                  <FieldLabelWithHelp
                    label={intl.formatMessage({
                      id: 'orgTools.field.config',
                      defaultMessage: 'Config (JSON)',
                    })}
                    help={intl.formatMessage({
                      id: 'orgTools.help.config',
                      defaultMessage:
                        'HTTP settings: http_method, url_template, headers, body_template, auth ({"type":"header"|"client_cert"|"none"}), tls_trust and response_path. Templates use {{args.*}} placeholders.',
                    })}
                  />
                  <TextArea
                    name="config"
                    value={formik.values.config}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    $tall
                    $error={!!(formik.touched.config && formik.errors.config)}
                  />
                  {renderFieldError('config')}
                </FieldGroup>
                <FieldGroup>
                  <FieldLabelWithHelp
                    label={intl.formatMessage({
                      id: 'orgTools.field.timeout',
                      defaultMessage: 'Timeout (ms)',
                    })}
                    help={intl.formatMessage({
                      id: 'orgTools.help.timeout',
                      defaultMessage:
                        'Maximum time to wait for the endpoint before the tool call fails, in milliseconds (e.g. 30000 = 30s).',
                    })}
                  />
                  <TextInput
                    name="timeoutMs"
                    value={formik.values.timeoutMs}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    $error={
                      !!(formik.touched.timeoutMs && formik.errors.timeoutMs)
                    }
                  />
                  {renderFieldError('timeoutMs')}
                </FieldGroup>
              </>
            )}

            {step === 2 && (
              <>
                <FieldGroup>
                  <FieldLabelWithHelp
                    label={
                      editing
                        ? intl.formatMessage({
                            id: 'orgTools.field.secretReplace',
                            defaultMessage:
                              'Secret (JSON) — leave blank to keep current',
                          })
                        : intl.formatMessage({
                            id: 'orgTools.field.secret',
                            defaultMessage:
                              'Secret (JSON, e.g. {"token":"..."})',
                          })
                    }
                    help={intl.formatMessage({
                      id: 'orgTools.help.secret',
                      defaultMessage:
                        'Credentials stored encrypted and never returned. Keys are referenced from config templates (e.g. {{secret.token}}). Use the client certificate section below for x509 client certs.',
                    })}
                  />
                  <TextArea
                    name="secret"
                    value={formik.values.secret}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder='{"token": "..."}'
                    $error={!!(formik.touched.secret && formik.errors.secret)}
                  />
                  {renderFieldError('secret')}
                </FieldGroup>
                <CertSection>
                  <SectionTitle>
                    {intl.formatMessage({
                      id: 'orgTools.mtls.title',
                      defaultMessage: 'Client Certificate (mTLS / x509)',
                    })}
                  </SectionTitle>
                  <HelpText>
                    {intl.formatMessage({
                      id: 'orgTools.mtls.help',
                      defaultMessage:
                        'For endpoints that require x509 client authentication. Set config auth to {"type":"client_cert"}. Providing a cert and key here overrides the JSON secret above and stores them encrypted.',
                    })}
                  </HelpText>
                  <FieldGroup>
                    <FieldLabelWithHelp
                      label={intl.formatMessage({
                        id: 'orgTools.mtls.cert',
                        defaultMessage: 'Client Certificate (PEM)',
                      })}
                      help={intl.formatMessage({
                        id: 'orgTools.help.mtls.cert',
                        defaultMessage:
                          'PEM-encoded client certificate presented during the TLS handshake. Upload a file or paste the -----BEGIN CERTIFICATE----- block.',
                      })}
                    />
                    <FileInput
                      type="file"
                      accept=".pem,.crt,.cer"
                      onChange={readFileInto('clientCert')}
                      data-testid="org-tool-cert-file"
                    />
                    <TextArea
                      name="clientCert"
                      value={formik.values.clientCert}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="-----BEGIN CERTIFICATE-----"
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabelWithHelp
                      label={intl.formatMessage({
                        id: 'orgTools.mtls.key',
                        defaultMessage: 'Client Private Key (PEM)',
                      })}
                      help={intl.formatMessage({
                        id: 'orgTools.help.mtls.key',
                        defaultMessage:
                          'PEM-encoded private key matching the certificate above. Stored encrypted. If the key is passphrase-protected, enter the passphrase below.',
                      })}
                    />
                    <FileInput
                      type="file"
                      accept=".pem,.key"
                      onChange={readFileInto('clientKey')}
                      data-testid="org-tool-key-file"
                    />
                    <TextArea
                      name="clientKey"
                      value={formik.values.clientKey}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="-----BEGIN PRIVATE KEY-----"
                      $error={
                        !!(formik.touched.clientKey && formik.errors.clientKey)
                      }
                    />
                    {renderFieldError('clientKey')}
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabelWithHelp
                      label={intl.formatMessage({
                        id: 'orgTools.mtls.passphrase',
                        defaultMessage: 'Key Passphrase (optional)',
                      })}
                      help={intl.formatMessage({
                        id: 'orgTools.help.mtls.passphrase',
                        defaultMessage:
                          'Passphrase that decrypts the private key. Leave blank if the key is unencrypted.',
                      })}
                    />
                    <TextInput
                      type="password"
                      name="clientKeyPassword"
                      value={formik.values.clientKeyPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      autoComplete="off"
                    />
                  </FieldGroup>
                </CertSection>
                <TestRow>
                  <ButtonAtom
                    variant={BUTTON_VARIANT_OUTLINE}
                    onClick={handleTest}
                    disabled={testing}
                    loading={testing}
                    stretch={false}
                  >
                    {intl.formatMessage({
                      id: 'orgTools.test',
                      defaultMessage: 'Test connection',
                    })}
                  </ButtonAtom>
                </TestRow>
                {testResult && (
                  <PromoBanner
                    dataTestId="org-tools-test-result"
                    tone={
                      testResult.ok
                        ? PROMO_BANNER_TONE_SUCCESS
                        : PROMO_BANNER_TONE_ERROR
                    }
                    ariaLive="polite"
                    icon={
                      <span className="material-symbols-rounded" aria-hidden>
                        {testResult.ok ? 'check_circle' : 'error'}
                      </span>
                    }
                    title={testResult.message}
                    description={formatTestMeta(testResult)}
                  />
                )}
              </>
            )}

            {apiError && <FormError>{apiError}</FormError>}
          </FormBody>
        </Dialog>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        tone={CONFIRM_DIALOG_TONE_DANGER}
        title={intl.formatMessage({
          id: 'orgTools.delete.title',
          defaultMessage: 'Delete Tool',
        })}
        description={intl.formatMessage(
          {
            id: 'orgTools.delete.message',
            defaultMessage: 'Delete tool "{name}"? This cannot be undone.',
          },
          { name: deleteTarget?.name ?? '' },
        )}
        confirmLabel={intl.formatMessage({
          id: 'orgTools.delete.confirm',
          defaultMessage: 'Delete',
        })}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        dataTestId="org-tools-delete-dialog"
      />
    </Wrapper>
  );
};

export default OrgTools;
